import { useState } from 'react';
import toast from 'react-hot-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faTrash,
  faPaperPlane,
  faToggleOn,
  faToggleOff,
} from '@fortawesome/free-solid-svg-icons';
import {
  Card,
  CardTitle,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
  TableColumn,
  Spinner,
  Paginator,
} from '@/components/ui';
import { Input } from '@/components/forms';
import {
  useAdminNumbers,
  useAddAdminNumber,
  useDeleteAdminNumber,
  useUpdateAdminNumber,
  useNotificationLogs,
  useTestNotification,
} from '@/api/useNotifications';
import styles from './Notifications.module.css';

const PAGE_SIZE = 15;

function StatusBadge({ success }: { success: boolean }) {
  return (
    <span className={`${styles.badge} ${success ? styles.badgeSuccess : styles.badgeFail}`}>
      {success ? 'Sent' : 'Failed'}
    </span>
  );
}

function TypeBadge({ type }: { type: string }) {
  const labels: Record<string, string> = {
    claude_balance: 'Claude',
    gemini_balance: 'Gemini',
    test: 'Test',
  };
  return <span className={styles.typeBadge}>{labels[type] || type}</span>;
}

export function Notifications() {
  const [phone, setPhone] = useState('');
  const [label, setLabel] = useState('');
  const [logPage, setLogPage] = useState(1);

  const { data: numbers, isLoading: numbersLoading } = useAdminNumbers();
  const { data: logs, isLoading: logsLoading } = useNotificationLogs(logPage);
  const addNumber = useAddAdminNumber();
  const deleteNumber = useDeleteAdminNumber();
  const updateNumber = useUpdateAdminNumber();
  const testNotification = useTestNotification();

  const handleAdd = () => {
    const trimmed = phone.trim();
    if (!trimmed) {
      toast.error('Phone number is required');
      return;
    }
    if (!trimmed.startsWith('+')) {
      toast.error('Phone must start with + (E.164 format, e.g. +51936282808)');
      return;
    }
    addNumber.mutate(
      { phone_number: trimmed, label: label.trim() },
      {
        onSuccess: () => {
          toast.success('Number added');
          setPhone('');
          setLabel('');
        },
        onError: () => toast.error('Failed to add number. It may already exist.'),
      },
    );
  };

  const handleDelete = (id: string, phoneNumber: string) => {
    if (!confirm(`Remove ${phoneNumber}?`)) return;
    deleteNumber.mutate(id, {
      onSuccess: () => toast.success('Number removed'),
      onError: () => toast.error('Failed to remove number'),
    });
  };

  const handleToggle = (id: string, currentActive: boolean) => {
    updateNumber.mutate(
      { id, is_active: !currentActive },
      {
        onSuccess: () => toast.success(`Number ${currentActive ? 'disabled' : 'enabled'}`),
        onError: () => toast.error('Failed to update'),
      },
    );
  };

  const handleTest = () => {
    testNotification.mutate(undefined, {
      onSuccess: (data) => toast.success(data.message),
      onError: () => toast.error('Test failed. Check Twilio configuration.'),
    });
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1>Admin Notifications</h1>
        <p className={styles.subtitle}>
          WhatsApp alerts when LLM API balance is low or exhausted
        </p>
      </div>

      {/* Admin Numbers Section */}
      <Card className={styles.section}>
        <div className={styles.sectionHeader}>
          <CardTitle>Alert Recipients</CardTitle>
          <button
            className={styles.testButton}
            onClick={handleTest}
            disabled={testNotification.isPending}
          >
            <FontAwesomeIcon icon={faPaperPlane} />
            {testNotification.isPending ? 'Sending...' : 'Send Test'}
          </button>
        </div>

        {/* Add form */}
        <div className={styles.addForm}>
          <div className={styles.addFormFields}>
            <Input
              name="phone"
              value={phone}
              setValue={setPhone}
              placeholder="+51936282808"
              label="Phone Number"
            />
            <Input
              name="label"
              value={label}
              setValue={setLabel}
              placeholder="Admin PE"
              label="Label (optional)"
            />
          </div>
          <button
            className={styles.addButton}
            onClick={handleAdd}
            disabled={addNumber.isPending}
          >
            <FontAwesomeIcon icon={faPlus} />
            Add
          </button>
        </div>

        {/* Numbers table */}
        {numbersLoading ? (
          <div className={styles.loading}>
            <Spinner size="md" />
          </div>
        ) : !numbers?.length ? (
          <p className={styles.empty}>No notification numbers configured.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableColumn>Phone</TableColumn>
              <TableColumn>Label</TableColumn>
              <TableColumn>Active</TableColumn>
              <TableColumn>Added</TableColumn>
              <TableColumn>Actions</TableColumn>
            </TableHeader>
            <TableBody>
              {numbers.map((n) => (
                <TableRow key={n.id}>
                  <TableCell>{n.phone_number}</TableCell>
                  <TableCell>{n.label || '—'}</TableCell>
                  <TableCell>
                    <button
                      className={styles.toggleButton}
                      onClick={() => handleToggle(n.id, n.is_active)}
                      title={n.is_active ? 'Disable' : 'Enable'}
                    >
                      <FontAwesomeIcon
                        icon={n.is_active ? faToggleOn : faToggleOff}
                        className={n.is_active ? styles.toggleOn : styles.toggleOff}
                      />
                    </button>
                  </TableCell>
                  <TableCell>{new Date(n.created_at).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <button
                      className={styles.deleteButton}
                      onClick={() => handleDelete(n.id, n.phone_number)}
                      title="Remove"
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      {/* Notification Log Section */}
      <Card className={styles.section}>
        <CardTitle>Notification Log</CardTitle>

        {logsLoading ? (
          <div className={styles.loading}>
            <Spinner size="md" />
          </div>
        ) : !logs?.results?.length ? (
          <p className={styles.empty}>No notifications sent yet.</p>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableColumn>Date</TableColumn>
                <TableColumn>Type</TableColumn>
                <TableColumn>Phone</TableColumn>
                <TableColumn>Message</TableColumn>
                <TableColumn>Status</TableColumn>
              </TableHeader>
              <TableBody>
                {logs.results.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell>
                      {new Date(log.created_at).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <TypeBadge type={log.notification_type} />
                    </TableCell>
                    <TableCell>{log.phone_number}</TableCell>
                    <TableCell>
                      <span className={styles.messageCell} title={log.message}>
                        {log.message.length > 80
                          ? `${log.message.slice(0, 80)}...`
                          : log.message}
                      </span>
                    </TableCell>
                    <TableCell>
                      <StatusBadge success={log.success} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {logs.count > PAGE_SIZE && (
              <Paginator
                page={logPage}
                numPages={Math.ceil(logs.count / PAGE_SIZE)}
                onPageChange={setLogPage}
              />
            )}
          </>
        )}
      </Card>
    </div>
  );
}
